import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  FinderResultModel,
  FinderResultsRequest,
  useGetFilmCinemas,
} from '@/services/movie-finder';
import { FC, memo, useEffect, useState } from 'react';

import CustomizedCircularProgress from '@/components/shared/customized-circular-progress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ExperienceCard } from '@/components/experience/experience-card';
import Icon24 from './icon-24';
import ImageBoxFuture from './image-box-future';

import { pxToRem } from '@/theme/typography';
import useFilmFormatter from '@/hooks/use-film-formatter';
import { useIntl } from 'react-intl';
import Link from 'next/link';
import OfferBadge from '../offers/offer-badge';
import OfferLottie from '../../lotties/muvi_offer.json';
import { OfferTag } from '../movie-details/showtimes/offer-tag';
import { useCity } from '@/logic/hooks';
import { useRouter } from 'next/router';

const TimeIcon = '/icons/movie-details/time.svg';
const pinIcon = '/icons/shared/location-icon-blue.svg';

export type MovieCollapseProps = {
  index: number;
  isExpanded: boolean;
  movie: FinderResultModel;
  filters: FinderResultsRequest;
  showExperiencesBy: 'cinema' | 'film';
  showLoader?: boolean;
  handleChange: (index: number) => void;
  callOnFilmHasBeenStarted: () => void;
  showTimeDate?: string;
};

const MovieCollapse: FC<MovieCollapseProps> = ({
  movie,
  index,
  isExpanded,
  showExperiencesBy,
  filters,
  handleChange,
  callOnFilmHasBeenStarted,
  showLoader = false,
  showTimeDate,
}) => {
  const theme = useTheme();
  const intl = useIntl();

  const [expandedMovie, setExpandedMovie] = useState<number>(movie.id);
  const [updatedMovie, setUpdatedMovie] = useState<FinderResultModel>(null);

  const { cityId: browserCityId } = useCity();
  const router = useRouter();
  const cityId = +router.query.cityId || browserCityId;

  const { genresWithDots, durationWithRateWithRtl, formattedDuration } =
    useFilmFormatter(movie);

  const { refetch, isFetching } = useGetFilmCinemas(
    {
      query: {
        id: expandedMovie,
      },
      params: {
        ...filters,
      },
    },
    {
      enabled: false,
      onSuccess: filmCinemas => {
        setUpdatedMovie(prevValue => ({
          ...prevValue,
          cinemas: filmCinemas?.cinemas,
        }));
      },
    }
  );

  useEffect(() => {
    setUpdatedMovie(movie);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movie]);

  useEffect(() => {
    isExpanded && !!showLoader && refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedMovie, isExpanded]);

  return (
    updatedMovie && (
      <>
        <Accordion
          expanded={isExpanded}
          onChange={() => {
            handleChange(index);
            setExpandedMovie(updatedMovie.id);
          }}
          sx={{
            mt: { tablet: pxToRem(32), mobile: '32px' },
          }}
        >
          <AccordionSummary
            expandIcon={
              isFetching && !!showLoader ? (
                <CustomizedCircularProgress size={30} />
              ) : (
                <ExpandMoreIcon sx={{ color: theme.palette.grey[90] }} />
              )
            }
            id={`movie-collapse-${updatedMovie.id}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'transparent',
              '& .MuiAccordionSummary-content': {
                my: '0px !important',
              },
              '& .MuiAccordionSummary-content.Mui-expanded': {
                mt: '15px !important',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                }}
              >
                <ImageBoxFuture
                  img={movie?.image}
                  title={movie?.title}
                  layout='intrinsic'
                  objectFit='cover'
                  width='1600px'
                  height='2400px'
                  sx={{
                    width: {
                      mobile: pxToRem(114),
                      tablet: pxToRem(136),
                    },
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: 'inset 0px 9px 30px rgba(0, 0, 0, 0.25)',
                  }}
                />

                {movie.rate && (
                  <Typography
                    component='span'
                    sx={{
                      color: updatedMovie.rate.fontColor,
                      backgroundColor: updatedMovie.rate.backgroundColor,
                      fontSize: pxToRem(10),
                      position: 'absolute',
                      top: pxToRem(14),
                      right: pxToRem(10),
                      paddingX: pxToRem(8),
                      paddingY: {
                        mobile: '2.5px',
                        mediumDesktop: '1px',
                      },
                      borderRadius: '20px',
                    }}
                  >
                    {updatedMovie.rate.title}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  marginInlineStart: pxToRem(28),
                }}
              >
                <Typography
                  component='h1'
                  sx={{
                    color: theme.palette.grey[80],
                    fontWeight: 700,
                    fontSize: { tablet: pxToRem(28), mobile: '18px' },
                    lineHeight: { tablet: pxToRem(32), mobile: '24px' },
                    marginTop: '10px',
                    display: '-webkit-box',
                    overflow: 'hidden',
                    WebkitLineClamp: '3',
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {updatedMovie.title}
                </Typography>
                <Box>
                  <Stack
                    direction='row'
                    sx={{
                      marginTop: pxToRem(6),
                      color: theme.palette.grey[50],
                      alignItems: 'center',
                    }}
                  >
                    {genresWithDots}
                  </Stack>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {formattedDuration && (
                      <Icon24 icon={TimeIcon} title='alt.time_icon' />
                    )}

                    <Typography
                      sx={{
                        fontSize: pxToRem(20),
                        color: theme.palette.grey[50],
                        lineHeight: '32px',
                        fontWeight: 400,
                        marginInlineStart: '8px',
                      }}
                    >
                      {durationWithRateWithRtl}
                    </Typography>
                  </Box>
                </Box>
                {movie?.hasOffer && <OfferTag />}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              maxWidth: pxToRem(700),
              marginBottom: '20px',
            }}
          >
            <Box
              sx={{
                my: pxToRem(30),

                mx: pxToRem(16),
              }}
            >
              {movie?.hasOffer && (
                <Link
                  href={{
                    pathname: `/movies/${movie?.uniqueName}/offers`,
                    // Omit cityId when unset instead of emitting `?cityId=`.
                    query: { date: showTimeDate, ...(cityId && { cityId }) },
                  }}
                  legacyBehavior
        passHref
                  prefetch={false}
                >
                  <Button component={'a'} sx={{ width: '100%' }}>
                    <OfferBadge
                      lottie={OfferLottie}
                      title={intl.formatMessage({ id: 'offer.showTime.title' })}
                      subTitle={intl.formatMessage({ id: 'offer.showTime.subtitle' })}
                      styles={{
                        container: { padding: '9px' },
                        title: {
                          fontSize: {
                            mobile: '12px',
                            tablet: pxToRem(16),
                          },
                        },
                        subTitle: {
                          fontSize: {
                            mobile: '12px',
                            tablet: pxToRem(16),
                          },
                          color: theme.palette.grey[60],
                        },
                      }}
                    />
                  </Button>
                </Link>
              )}
            </Box>

            {showExperiencesBy === 'film' &&
              updatedMovie.experiances?.map((experience, index: number) => (
                <Box
                  key={experience.id}
                  sx={{
                    margin: { mobile: '16px', tablet: pxToRem(16) },
                  }}
                >
                  <ExperienceCard
                    movieId={updatedMovie.id}
                    experience={experience}
                    index={index}
                    sxProps={{
                      marginTop: 0,
                    }}
                    callOnFilmHasBeenStarted={callOnFilmHasBeenStarted}
                  />
                </Box>
              ))}

            {showExperiencesBy === 'cinema' &&
              updatedMovie.cinemas?.map(({ name, experiences, id }) => (
                <Box
                  key={id}
                  sx={{
                    margin: { mobile: '16px', tablet: '0px' },
                  }}
                >
                  <Stack sx={{ marginY: 3 }} direction='row' alignItems={'center'}>
                    <ImageBoxFuture
                      img={pinIcon}
                      title={intl.formatMessage({
                        id: 'alt.pin_icon',
                      })}
                      objectFit='contain'
                      sx={{
                        width: '14px',
                        height: '14px',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: {
                          mobile: '18px',
                          tablet: pxToRem(20),
                        },
                        marginLeft: 2,
                        color: theme.palette.grey[80],
                      }}
                    >
                      {name}
                    </Typography>
                  </Stack>

                  <Box>
                    {experiences?.map((experience, index: number) => (
                      <Box key={experience.id}>
                        <ExperienceCard
                          movieId={updatedMovie.id}
                          experience={experience}
                          index={index}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
          </AccordionDetails>
        </Accordion>
        <Divider
          sx={{
            mt: { tablet: pxToRem(24), mobile: '24px' },
            mr: pxToRem(10),
            ml: pxToRem(35),
          }}
        />
      </>
    )
  );
};

export default memo(MovieCollapse);
